import React, { useMemo, useRef } from 'react';

const moveFocus = (buttons, index) => {
  const target = buttons[index];
  if (target && typeof target.focus === 'function') {
    target.focus();
  }
};

export function Tabs({
  tabs,
  active,
  onChange,
  ariaLabel = 'Sections',
  className = '',
  idBase = 'sp-tabs'
}) {
  const buttonRefs = useRef([]);
  const tabEntries = useMemo(
    () =>
      (tabs || []).map((tab) => ({
        ...tab,
        tabId: `${idBase}-tab-${tab.id}`,
        panelId: `${idBase}-panel-${tab.id}`
      })),
    [tabs, idBase]
  );

  const activeIndex = Math.max(
    0,
    tabEntries.findIndex((tab) => tab.id === active)
  );

  const onKeyDown = (event, index) => {
    if (!tabEntries.length) return;
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    let nextIndex = index;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabEntries.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabEntries.length) % tabEntries.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabEntries.length - 1;
    const nextTab = tabEntries[nextIndex];
    if (nextTab) {
      onChange(nextTab.id);
      moveFocus(buttonRefs.current, nextIndex);
    }
  };

  return (
    <div className={`sp-tabs ${className}`.trim()} role="tablist" aria-label={ariaLabel}>
      {tabEntries.map((tab, index) => (
        <button
          key={tab.id}
          id={tab.tabId}
          type="button"
          role="tab"
          aria-selected={active === tab.id}
          aria-controls={tab.panelId}
          tabIndex={index === activeIndex ? 0 : -1}
          ref={(node) => {
            buttonRefs.current[index] = node;
          }}
          onKeyDown={(event) => onKeyDown(event, index)}
          onClick={() => onChange(tab.id)}
          className={`sp-tab ${active === tab.id ? 'sp-tab--active' : ''}`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function TabPanel({ active, id, children, idBase = 'sp-tabs', className = 'pt-6' }) {
  const isActive = active === id;
  return (
    <section
      id={`${idBase}-panel-${id}`}
      role="tabpanel"
      aria-labelledby={`${idBase}-tab-${id}`}
      hidden={!isActive}
      className={className}
    >
      {isActive ? children : null}
    </section>
  );
}
